/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useEffect, useState, useRef, useLayoutEffect } from 'react';

import failedFavicon from '@fai/asset/favicon/favicon-failed.svg';
import pendingFavicon from '@fai/asset/favicon/favicon-pending.svg';
import successFavicon from '@fai/asset/favicon/favicon-success.svg';
import defaultFavicon from '@fai/asset/favicon/favicon.svg';

export type FaviconType = 'default' | 'success' | 'pending' | 'failed';

// ref: https://github.com/mantinedev/mantine/blob/master/packages/%40mantine/hooks/src/use-favicon/use-favicon.ts
export const useChangeFavicon = (type: FaviconType) => {
  const [favicon, setFavicon] = useState<string>(defaultFavicon);
  const link = useRef<HTMLLinkElement>();

  useLayoutEffect(() => {
    if (!link.current) {
      const existingElements = document.querySelectorAll<HTMLLinkElement>('link[rel*="icon"]');
      existingElements.forEach((element) => document.head.removeChild(element));

      const element = document.createElement('link');
      element.rel = 'shortcut icon';
      link.current = element;
      document.querySelector('head')?.appendChild(element);
    }

    link.current.setAttribute('type', 'image/svg+xml');
    link.current.setAttribute('href', favicon);

    return () => {
      link.current?.setAttribute('href', defaultFavicon);
    };
  }, [favicon]);

  useEffect(() => {
    switch (type) {
      case 'success': {
        setFavicon(successFavicon);

        break;
      }
      case 'pending': {
        setFavicon(pendingFavicon);

        break;
      }
      case 'failed': {
        setFavicon(failedFavicon);

        break;
      }

      default: {
        setFavicon(defaultFavicon);

        break;
      }
    }
  }, [type]);
};
